import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { ProductExportContent } from './ProductExportContent';
import type { ProductExportProps } from './useProductExport';

/**
 * Export Excel (bos-app#120): the toolbar's button and the notice a run
 * leaves, as a section of its own. It owns the run, its state and the notice;
 * the page hands it only what the list shows (`scope`).
 *
 * The boundary keeps a render crash here from taking the toolbar with it
 * (rulebook §12). Its Try again renders the section again; nothing here is
 * loaded, so there is nothing to read again first. It also resets by itself
 * when the export scope changes, since the section then renders from new
 * input (bos-app#139).
 */
export const ProductExport: React.FC<ProductExportProps> = ({ scope }) => {
    const { t } = useTranslation('pages');

    return (
        <ErrorBoundary
            data-testid="product-export-error-boundary"
            fallbackMessage={t('products.export.crashedMessage')}
            id="product-export-error-boundary"
            resetKeys={[scope]}
            retryLabel={t('products.export.crashedRetry')}
            title={t('products.export.crashedTitle')}
        >
            <ProductExportContent scope={scope} />
        </ErrorBoundary>
    );
};
